import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import HeroCover from "@/components/home/HeroCover";
import { SHI_SHEET_URL } from "@/lib/images";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <span className="twss-mask-line" style={{ animationDelay: `${delay}s` }}>
      {children}
    </span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16 md:pt-24"
    >
      <div className="glow-ember pointer-events-none absolute -top-40 -left-40 h-[36rem] w-[36rem]" aria-hidden="true" />
      <div className="glow-wine pointer-events-none absolute bottom-0 right-0 h-[30rem] w-[30rem]" aria-hidden="true" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-10">
        {/* Title sequence — the typography is the branding here, no logo needed */}
        <div className="relative z-10 flex flex-col justify-center lg:col-span-7">
          <p
            className="twss-rise font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-gold md:text-xs"
            style={{ animationDelay: "0.05s" }}
          >
            A Culture Show
          </p>

          <h1
            data-testid="hero-title"
            className="relative mt-6 font-display font-black uppercase leading-[0.84] tracking-tight text-[clamp(3.2rem,9.5vw,7.5rem)]"
          >
            <MaskedLine delay={0.15}>That&rsquo;s</MaskedLine>
            <MaskedLine delay={0.27}>
              What <span className="italic text-neon">SHi</span>
            </MaskedLine>
            <MaskedLine delay={0.39}>Said.</MaskedLine>
          </h1>

          <p
            className="neon-text neon-buzz twss-rise mt-3 self-start font-editorial italic text-3xl md:text-4xl"
            style={{ animationDelay: "0.7s", transform: "rotate(-3deg)" }}
          >
            with Ashyla
          </p>

          <p
            className="twss-rise mt-8 max-w-xl text-base leading-relaxed text-latte md:text-lg"
            style={{ animationDelay: "0.85s" }}
          >
            I&rsquo;m SHi. I talk pop culture, music, relationships, and
            whatever&rsquo;s been sitting on my mind longer than it should.
          </p>

          <div
            className="twss-rise mt-10 flex flex-wrap items-center gap-4"
            style={{ animationDelay: "1s" }}
          >
            <a
              href={SHI_SHEET_URL}
              target="_blank"
              rel="noopener"
              data-testid="hero-cta-sheet"
              className="inline-flex items-center gap-2 rounded-full border border-gold bg-neon px-8 py-4 font-bold text-ink transition-colors hover:bg-cream"
            >
              Get The Shi Sheet
            </a>
            <Link
              to="/shows"
              data-testid="hero-cta-shows"
              className="rounded-full border border-gold/50 px-8 py-4 font-semibold text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              See the Shows
            </Link>
          </div>

          <div
            className="twss-rise mt-12 flex flex-wrap items-center gap-4"
            style={{ animationDelay: "1.15s" }}
          >
            <span
              data-testid="hero-premiere-date"
              className="inline-flex items-center gap-2.5 rounded-full border border-corduroy bg-cocoa px-5 py-2.5 font-sans text-[11px] font-bold uppercase tracking-[0.2em] text-cream"
            >
              <span className="h-2 w-2 rounded-full bg-ember animate-pulse-dot" />
              Premieres Thursday, December 3, 2026.
            </span>
          </div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative twss-rise" >
            <HeroCover />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
